'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { getSubscription } from '@/api/usage';
import { getStripeBillingUrl } from '@/api/stripe';
import {
  getSubscriptionState,
  sidebarPlanCardStateClassNames,
  subscriptionStateTranslationKeys,
} from '@/constants/color';
import { useToast } from '@/app/hooks/use-toast';
import {
  isPostpaidPlan as checkIsPostpaidPlan,
  isPrepaidPlan as checkIsPrepaidPlan,
} from '@/app/lib/billing';
import { useState } from 'react';
import { Loader } from 'lucide-react';
import { useIntercom } from './IntercomProvider';
import PlanBalanceCard from './PlanBalanceCard';
import PlanBalanceCardSkeleton from './PlanBalanceCardSkeleton';
import type { PillColor } from '@/app/components/ui/Pill';

type SidebarPlanCardProps = {
  planHref: string;
  clientId: string;
};

export default function SidebarPlanCard({ planHref, clientId }: SidebarPlanCardProps) {
  const t = useTranslations('sidebarNav');
  const tPlanCard = useTranslations('sidebarNav.planCard');
  const tPlans = useTranslations('settings.plansPage');
  const { toast } = useToast();
  const { trackEvent } = useIntercom();
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const { data: subscriptionData, isPending } = useQuery({
    queryKey: ['billing-subscription', clientId],
    queryFn: () => getSubscription(clientId),
    enabled: !!clientId,
  });

  const isPostpaidPlan = checkIsPostpaidPlan(subscriptionData?.plan);
  const isPrepaidPlan = checkIsPrepaidPlan(subscriptionData?.plan);
  const planName = isPostpaidPlan
    ? t('postpaidPlan')
    : isPrepaidPlan
      ? t('prepaidPlan')
      : subscriptionData?.plan?.name
        ? subscriptionData.plan.name
        : '';
  const remainingCredits =
    subscriptionData?.credit_usage?.remaining_credits ??
    subscriptionData?.last_cycle_credit_usage?.remaining_credits ??
    0;
  const consumedCredits =
    subscriptionData?.credit_usage?.consumed_credits ??
    subscriptionData?.last_cycle_credit_usage?.consumed_credits ??
    0;
  const progress = Number(
    subscriptionData?.credit_usage?.consumed_credits_percentage ??
      subscriptionData?.last_cycle_credit_usage?.consumed_credits_percentage ??
      0
  );
  const showPlanCta = Boolean(subscriptionData?.plan);
  const cardState = isPostpaidPlan
    ? 'active'
    : getSubscriptionState({
        status: subscriptionData?.status,
        remainingCredits,
        usagePercentage: progress,
      });
  const cardClassNames = sidebarPlanCardStateClassNames[cardState];
  const statusTranslationKey = subscriptionStateTranslationKeys[cardState];
  const isActiveStatus = statusTranslationKey === 'active';
  const isPaymentFailed = cardState === 'payment_failed';
  const ctaLabel = isPaymentFailed ? tPlanCard('fixPaymentMethod') : t('manage');
  const ctaEventName = isPaymentFailed ? 'plan_card_fix_payment_method' : 'nav_menu_credits_manage';

  async function handleFixPayment() {
    try {
      setIsPaymentLoading(true);
      const { billing_url } = await getStripeBillingUrl(clientId);
      window.open(billing_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast({
        title: tPlans('billingUnavailable'),
        description:
          error instanceof Error ? error.message : tPlans('billingUnavailableDescription'),
        toastType: 'error',
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }

  if (isPending) {
    return <PlanBalanceCardSkeleton />;
  }

  if (!subscriptionData) {
    return <></>;
  }

  const action = showPlanCta ? (
    isPaymentFailed ? (
      <button
        type="button"
        disabled={isPaymentLoading}
        onClick={() => {
          trackEvent(ctaEventName);
          void handleFixPayment();
        }}
        className="shrink-0 text-sm font-medium text-primary disabled:opacity-60"
      >
        {isPaymentLoading ? <Loader className="h-4 w-4 animate-spin" /> : ctaLabel}
      </button>
    ) : (
      <Link
        href={planHref}
        className="shrink-0 text-sm font-medium text-primary"
        onClick={() => trackEvent(ctaEventName)}
      >
        {ctaLabel}
      </Link>
    )
  ) : undefined;

  return (
    <PlanBalanceCard
      planName={planName}
      credits={isPostpaidPlan ? consumedCredits : remainingCredits}
      creditsLabel={isPostpaidPlan ? t('creditsConsumed') : t('creditsAvailable')}
      statusLabel={
        isPostpaidPlan
          ? tPlanCard('status.active')
          : subscriptionData.status
            ? tPlanCard(`status.${statusTranslationKey}`)
            : undefined
      }
      statusColor={(isActiveStatus ? 'success' : cardClassNames.pill) as PillColor}
      action={action}
      className={cardClassNames.card}
    />
  );
}
